import { isDisplayP3Supported } from '../utils/p3.js';

// CSS color functions that can describe colors outside the sRGB gamut. Named colors, hex,
// `rgb()`, `hsl()`, and `hwb()` are all sRGB, so they don't need the wider-gamut path.
const WIDE_GAMUT_RE = /\b(?:color|oklab|oklch|lab|lch)\(/i;
/** Whether a CSS color string uses a function that can express colors outside sRGB. */
function isWideGamutColor(color) {
    return WIDE_GAMUT_RE.test(color);
}
/**
 * Resolve the `color` prop to concrete channels plus the color space to render them in.
 *
 * An `{ r, g, b }` object has no inherent color space, so it's taken as-is in the requested
 * `colorSpace`. A string carries its own gamut, so `colorSpace` is ignored for it: a
 * wide-gamut string is read back in `display-p3` (when the display supports it), everything
 * else in `srgb`.
 */
function resolveOpacityColor(color, colorSpace) {
    if (typeof color !== 'string') {
        return { rgb: color, colorSpace };
    }
    const resolved = isWideGamutColor(color) && isDisplayP3Supported() ? 'display-p3' : 'srgb';
    return { rgb: readCssColor(color, resolved), colorSpace: resolved };
}
// At most one context per color space, reused across every resolution. (The "too many
// contexts" ceiling is a WebGL thing; 2d canvases have no such cap, and we keep this to two.)
const contexts = new Map();
/**
 * Paint the color into a 1x1 canvas and read its channels back. Any CSS color the browser can
 * parse works. A string the browser can't parse leaves the pixel transparent black, i.e.
 * `{ 0, 0, 0 }`.
 */
function readCssColor(color, colorSpace) {
    const ctx = contextFor(colorSpace);
    if (!ctx) {
        return { r: 0, g: 0, b: 0 };
    }
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1, { colorSpace }).data;
    return { r: r / 255, g: g / 255, b: b / 255 };
}
function contextFor(colorSpace) {
    const cached = contexts.get(colorSpace);
    if (cached) {
        return cached;
    }
    if (typeof document === 'undefined') {
        return null;
    }
    const ctx = document
        .createElement('canvas')
        .getContext('2d', { colorSpace, willReadFrequently: true });
    if (ctx) {
        contexts.set(colorSpace, ctx);
    }
    return ctx;
}

export { isWideGamutColor, resolveOpacityColor };
//# sourceMappingURL=opacity-slider.color.js.map
